/**
 * Outbox v2 — alerting (ATL-1445 stage 3, P3; plan §2 "Alerting").
 *
 * The queue never drops, so its failure mode is not loss but a STOP: one held entry blocks every
 * write behind it on that device (plan §9 risk 3, accepted). A stop nobody sees is a loss by
 * another name, so every stop is loud in three places:
 *
 *  - Sentry `error`, fingerprinted `['outbox-v2-held', label, status]` (and `['outbox-v2-blocked',
 *    kind]` for an unreadable record, `['outbox-v2-escalated', label, status]` for a head that has
 *    retried for over an hour while online — plan §9 risk 3). Raised on the TRANSITION into held, or
 *    when the reason changes — never on each 30-minute retry of the same held entry; blocked records
 *    and escalations are deduplicated through the persisted `OutboxAlertLedger`, so a restart does
 *    not re-page. The Sentry allowance is 5,000 events a month and ran dry for nine days in
 *    September (ATL-1354, `operationalTelemetry.ts`).
 *  - PostHog `outbox_v2_held` / `outbox_v2_blocked` (countable per organization and release even
 *    while Sentry refuses events), the super-properties `outbox_v2_depth` and
 *    `outbox_v2_oldest_age_ms` on every event (the after-flip check of design §5), and an hourly
 *    `outbox_v2_state`.
 *  - The in-app banner (`components/OutboxV2Banner.js`), which reads the drainer's status.
 *
 * PostHog properties are kinds and counts only (`operationalTelemetry.ts`'s rule): label, reason,
 * status, code, origin, ages. Entry and document ids go to the Sentry context and the console, where
 * an incident responder has them. The one exception is the operator ADOPTION record (P2 obligation
 * 3), which must carry the entry id, the previous install id, the actor and the reason: the store
 * deletes the entry once delivered, so this event is the durable audit trail of who vouched for it.
 *
 * The drainer takes an `OutboxAlertSink`, so it is tested without Sentry or PostHog; the real sink is
 * `createOutboxAlertSink`.
 */
import AsyncStorage from '@react-native-async-storage/async-storage'
import * as Sentry from '@sentry/react-native'

import { captureOperationalEvent } from '../analytics/operationalTelemetry'
import type { HeldReason } from './outboxClassify'
import type { OutboxEntryOrigin, OutboxWriteKind } from './outboxEntry'
import type { PurgeWithheldReason } from './outboxStore'

export interface OutboxHeldAlert {
  label: string
  kind: OutboxWriteKind
  origin: OutboxEntryOrigin
  reason: HeldReason
  status: number | null
  code: string | null
  attempts: number
  /** ms since the writer queued it. */
  ageMs: number
  /** Only an operator may release it (an absolute stock request; review 1 S4, review 2). */
  operatorOnly: boolean
  entryId: string
  documentId: string
}

export interface OutboxEscalationAlert {
  label: string
  /** Age of the oldest entry. */
  ageMs: number
  depth: number
  /** The head's state (`pending`, `held`, `in-flight`) or `corrupt`/`stray`. */
  state: string
  status: number | null
  code: string | null
  attempts: number
}

export interface OutboxBlockedAlert {
  kind: 'corrupt' | 'stray'
  reason: string
  /** Engine handle for the operator (`removeCorrupt`). */
  ref: string
}

/**
 * P5 (stage-3 §4): a write rollback (or a read rollback while writes are Supabase-master) that this
 * device cannot complete, because outbox v2 cannot be drained to empty. The session keeps running
 * and keeps the restart unarmed until the queue empties; only a person can unblock it.
 */
export interface OutboxRollbackBlockedAlert {
  /** The watcher action that is waiting (`write-rollback`, `read-rollback-after-v2-drain`). */
  action: string
  /** What stops the drain: a held entry, an unreadable record, a foreign-install hold, or no store. */
  /**
   * What stops the drain. `drain-cap-expired` (review 1): a read rollback's capped drain ran out and
   * the restart went ahead with v2 not drained — the restarted session drains it.
   */
  reason: 'held' | 'unreadable-record' | 'foreign-install' | 'outbox-unavailable' | 'drain-cap-expired'
  /** The queue's counts; null when it could not be read (never 0 for "unknown"). */
  depth: number | null
  held: number | null
  blocked: number | null
  foreignInstall: number | null
  /** ms since the oldest readable entry was queued, when known. */
  oldestAgeMs: number | null
}

export interface OutboxStateReport {
  depth: number
  held: number
  blocked: number
  foreignInstall: number
  oldestAgeMs: number | null
  paused: 'master-changed' | null
  escalated: boolean
  /** Organizations other than the drained one that still have records on this device. */
  otherOrganizations: number
  /** Records attributable to no organization (census). */
  unattributed: number
}

export interface OutboxAlertSink {
  held(alert: OutboxHeldAlert): void
  blocked(alert: OutboxBlockedAlert): void
  escalated(alert: OutboxEscalationAlert): void
  masterChanged(alert: { label: string; status: number | null; code: string }): void
  deletePurge(report: { label: string; purged: number; purgeWithheld: PurgeWithheldReason | null; purgeIncomplete: boolean }): void
  enqueueUnresolved(report: { label: string; kind: 'indeterminate' | 'corrupt-duplicate' }): void
  adopted(record: { entryId: string; label: string; fromInstallId: string; actor: string; reason: string }): void
  /** An operator released or discarded an operator-only hold (same audit rule as `adopted`). */
  operatorResolved(record: { entryId: string; label: string; decision: 'release' | 'discard'; actor: string; reason: string }): void
  superProperties(props: { outbox_v2_depth: number; outbox_v2_oldest_age_ms: number | null }): void
  state(report: OutboxStateReport): void
  /** P5: a rollback waiting on a queue that cannot drain (pages; once per reason per rollback). */
  rollbackBlocked(alert: OutboxRollbackBlockedAlert): void
  /**
   * P5 review 1: startup had to look at v2 (it can hold master writes) and could not read it. The
   * session drains as soon as the store answers; until then nobody knows what is undelivered.
   */
  startupLookFailed(alert: { mode: 'firestore' | 'supabase' | 'refuse'; writesMaster: 'firestore' | 'supabase' }): void
}

function splitLabel(label: string): { module: string; op: string } {
  const i = label.indexOf('.')
  return i === -1 ? { module: label, op: '' } : { module: label.slice(0, i), op: label.slice(i + 1) }
}

/** An Error only so Sentry groups a condition that needs a person; its stack is not the point. */
class OutboxV2StopError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'OutboxV2StopError'
  }
}

export interface OutboxAlertSinkOptions {
  /** PostHog super-properties (`posthog.register`); omitted until the provider is mounted. */
  register?: (props: Record<string, number | null>) => unknown
}

export function createOutboxAlertSink(options: OutboxAlertSinkOptions = {}): OutboxAlertSink {
  return {
    held(alert) {
      const { module, op } = splitLabel(alert.label)
      const status = alert.status === null ? 'none' : String(alert.status)
      // eslint-disable-next-line no-console
      console.error(`[outboxV2] HELD ${alert.label} (${alert.reason}, ${status}) — the queue is stopped`, JSON.stringify(alert))
      Sentry.captureException(new OutboxV2StopError(`outbox v2 held ${alert.label} (${alert.reason}, ${status})`), {
        level: 'error',
        fingerprint: ['outbox-v2-held', alert.label, status],
        tags: {
          outbox_v2_held: true,
          outbox_v2_reason: alert.reason,
          outbox_v2_origin: alert.origin,
          dual_write: alert.label,
          dual_write_module: module,
          dual_write_op: op,
          ...(alert.code ? { outbox_v2_code: alert.code } : {}),
        },
        contexts: { outbox_v2: { ...alert } },
      })
      captureOperationalEvent('outbox_v2_held', {
        label: alert.label,
        module,
        op,
        kind: alert.kind,
        origin: alert.origin,
        reason: alert.reason,
        status: alert.status,
        code: alert.code,
        attempts: alert.attempts,
        age_ms: alert.ageMs,
        operator_only: alert.operatorOnly,
      })
    },
    escalated(alert) {
      const { module, op } = splitLabel(alert.label)
      const status = alert.status === null ? 'none' : String(alert.status)
      // eslint-disable-next-line no-console
      console.error(`[outboxV2] ESCALATED ${alert.label}: oldest entry ${alert.ageMs} ms old while online`, JSON.stringify(alert))
      Sentry.captureException(new OutboxV2StopError(`outbox v2 not delivering for over an hour (${alert.label}, ${status})`), {
        level: 'error',
        fingerprint: ['outbox-v2-escalated', alert.label, status],
        tags: { outbox_v2_escalated: true, outbox_v2_state: alert.state, dual_write: alert.label, dual_write_module: module, dual_write_op: op },
        contexts: { outbox_v2: { ...alert } },
      })
      captureOperationalEvent('outbox_v2_escalated', {
        label: alert.label,
        module,
        op,
        state: alert.state,
        status: alert.status,
        code: alert.code,
        attempts: alert.attempts,
        depth: alert.depth,
        age_ms: alert.ageMs,
      })
    },
    blocked(alert) {
      // eslint-disable-next-line no-console
      console.error(`[outboxV2] BLOCKED by a ${alert.kind} record (${alert.reason})`, alert.ref)
      Sentry.captureException(new OutboxV2StopError(`outbox v2 blocked by a ${alert.kind} record`), {
        level: 'error',
        fingerprint: ['outbox-v2-blocked', alert.kind],
        tags: { outbox_v2_blocked: alert.kind },
        contexts: { outbox_v2: { ...alert } },
      })
      captureOperationalEvent('outbox_v2_blocked', { kind: alert.kind, reason: alert.reason.slice(0, 120) })
    },
    rollbackBlocked(alert) {
      // eslint-disable-next-line no-console
      console.error(
        `[outboxV2] ROLLBACK BLOCKED (${alert.action}, ${alert.reason}) — this device keeps its write master until the queue drains`,
        JSON.stringify(alert),
      )
      Sentry.captureException(new OutboxV2StopError(`outbox v2 blocks a ${alert.action} (${alert.reason})`), {
        level: 'error',
        fingerprint: ['outbox-v2-rollback-blocked', alert.action, alert.reason],
        tags: { outbox_v2_rollback_blocked: alert.reason, outbox_v2_rollback_action: alert.action },
        contexts: { outbox_v2: { ...alert } },
      })
      captureOperationalEvent('outbox_v2_rollback_blocked', {
        action: alert.action,
        reason: alert.reason,
        depth: alert.depth,
        held: alert.held,
        blocked: alert.blocked,
        foreign_install: alert.foreignInstall,
        oldest_age_ms: alert.oldestAgeMs,
      })
    },
    startupLookFailed(alert) {
      // eslint-disable-next-line no-console
      console.error(`[outboxV2] the startup look at the queue FAILED (${alert.mode}, ${alert.writesMaster}) — undelivered master writes may be waiting`)
      Sentry.captureException(new OutboxV2StopError(`outbox v2 unreadable at startup (${alert.mode})`), {
        level: 'error',
        fingerprint: ['outbox-v2-startup-look-failed', alert.mode, alert.writesMaster],
        tags: { outbox_v2_startup_look_failed: alert.mode },
      })
      captureOperationalEvent('outbox_v2_startup_look_failed', { mode: alert.mode, writes_master: alert.writesMaster })
    },
    masterChanged(alert) {
      Sentry.addBreadcrumb({ category: 'outbox-v2', level: 'warning', message: `master changed (${alert.code})`, data: alert })
      captureOperationalEvent('outbox_v2_master_changed', { label: alert.label, status: alert.status, code: alert.code })
    },
    deletePurge(report) {
      captureOperationalEvent('outbox_v2_delete_purge', {
        label: report.label,
        purged: report.purged,
        purge_withheld: report.purgeWithheld,
        purge_incomplete: report.purgeIncomplete,
      })
    },
    enqueueUnresolved(report) {
      Sentry.captureException(new OutboxV2StopError(`outbox v2 enqueue unresolved: ${report.kind}`), {
        level: 'error',
        fingerprint: ['outbox-v2-enqueue-unresolved', report.label, report.kind],
        tags: { outbox_v2_enqueue_unresolved: report.kind, dual_write: report.label },
      })
      captureOperationalEvent('outbox_v2_enqueue_unresolved', { label: report.label, kind: report.kind })
    },
    adopted(record) {
      captureOperationalEvent('outbox_v2_foreign_adopted', {
        entry_id: record.entryId,
        label: record.label,
        from_install_id: record.fromInstallId,
        actor: record.actor,
        reason: record.reason.slice(0, 500),
      })
    },
    operatorResolved(record) {
      captureOperationalEvent('outbox_v2_operator_resolved', {
        entry_id: record.entryId,
        label: record.label,
        decision: record.decision,
        actor: record.actor,
        reason: record.reason.slice(0, 500),
      })
    },
    superProperties(props) {
      try {
        void options.register?.(props)
      } catch {
        // A telemetry failure never touches the queue.
      }
    },
    state(report) {
      captureOperationalEvent('outbox_v2_state', {
        depth: report.depth,
        held: report.held,
        blocked: report.blocked,
        foreign_install: report.foreignInstall,
        oldest_age_ms: report.oldestAgeMs,
        paused: report.paused,
        escalated: report.escalated,
        other_organizations: report.otherOrganizations,
        unattributed: report.unattributed,
      })
    },
  }
}

/**
 * Persisted "already paged" keys (review 1 nit): without it every restart re-pages the same unreadable
 * record or the same escalated head, which spends the Sentry allowance on one incident. Method names
 * are `seen`/`remember` on purpose — the W-writer census reads `.add(x)` as a Firestore write shape.
 */
export interface OutboxAlertLedger {
  seen(key: string): Promise<boolean>
  remember(key: string): Promise<void>
}

export const ALERT_LEDGER_STORAGE_KEY = 'outboxV2.alertLedger.v1'
/** Keys kept (oldest forgotten first): a forgotten key can only re-page, never silence. */
export const ALERT_LEDGER_MAX_KEYS = 200

/** AsyncStorage-backed: tiny, bounded metadata, never queue data (the queue is outbox v2's store). */
export function createAsyncStorageAlertLedger(): OutboxAlertLedger {
  const read = async (): Promise<string[]> => {
    const raw = await AsyncStorage.getItem(ALERT_LEDGER_STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === 'string') : []
  }
  return {
    async seen(key) {
      return (await read()).includes(key)
    },
    async remember(key) {
      const keys = (await read()).filter((k) => k !== key)
      keys.push(key)
      await AsyncStorage.setItem(ALERT_LEDGER_STORAGE_KEY, JSON.stringify(keys.slice(-ALERT_LEDGER_MAX_KEYS)))
    },
  }
}
